import Select, { components } from "react-select";
import { Check } from "lucide-react";
import { reactSelectStylesAjax } from "../../others/helperFunctions";
import { memo, useMemo } from "@wordpress/element";

// Custom Option component
const CustomOptionBase = (props) => {
    return (
        <components.Option {...props}>
            <div className="flex items-center gap-3 py-1">
                <img
                    src={props.data.image}
                    alt={props.data.label}
                    className={`${props.imgWidth || "w-10"} ${
                        props.imgheight || "h-auto"
                    } object-contain`}
                    style={{ backgroundColor: "transparent" }}
                />
                <span className="text-gray-800">{props.data.label}</span>
                {props.isSelected && (
                    <Check className="ml-auto w-4 h-4 text-blue-600" />
                )}
            </div>
        </components.Option>
    );
};

const CustomOption = memo(
    CustomOptionBase,
    (prev, next) =>
        prev.data?.value === next.data?.value &&
        prev.isSelected === next.isSelected &&
        prev.imgWidth === next.imgWidth &&
        prev.imgheight === next.imgheight
);

// Custom SingleValue component (shows selected value with image inside control)
const CustomSingleValueBase = (props) => {
    return (
        <components.SingleValue {...props}>
            <div className="flex items-center gap-2">
                {props.data?.image && (
                    <img
                        src={props.data.image}
                        alt={props.data.label}
                        className={`${props.imgWidth || "w-5"} ${
                            props.imgheight || "h-5"
                        } object-contain rounded-sm`}
                        style={{ backgroundColor: "transparent" }}
                    />
                )}
                <span>{props.data?.label}</span>
            </div>
        </components.SingleValue>
    );
};

const CustomSingleValue = memo(
    CustomSingleValueBase,
    (prev, next) =>
        prev.data?.value === next.data?.value &&
        prev.imgWidth === next.imgWidth &&
        prev.imgheight === next.imgheight
);

// Reusable Custom Image Select Component
const ImageSelect = ({
    options,
    value,
    onChange,
    name,
    placeholder = "Select an option...",
    isSearchable = false,
    isDisabled = false,
    isClearable = false,
    isMulti = false,
    width,
    height = "70px",
    imgWidth = "w-auto",
    imgheight = "h-14",
    className = "",
    styles = {},
    ...props
}) => {
    const mergedStyles = {
        ...reactSelectStylesAjax({ width, isMulti, height }),
        ...styles,
    };
    // react-select expects the `value` prop to be the option object (or array of option objects when isMulti)
    // but callers often pass a primitive value (e.g. 'react'). Normalize here.
    const resolveValueProp = () => {
        if (isMulti) {
            if (!value) return [];
            // If value is an array of primitives, map to option objects
            if (Array.isArray(value)) {
                return value
                    .map((v) => options.find((opt) => opt.value === v))
                    .filter(Boolean);
            }
            // If it's already an array of objects, return as-is
            return value;
        }

        if (value === undefined || value === null || value === "") return null;

        // If value is an object with `value` prop, assume it's the option
        if (typeof value === "object" && value.value !== undefined)
            return value;

        // Otherwise assume primitive and find matching option
        return options.find((opt) => opt.value === value) || null;
    };

    const handleChange = (selected) => {
        // selected can be option object, array (for multi) or null when cleared
        if (typeof onChange !== "function") return;

        if (isMulti) {
            const vals = Array.isArray(selected)
                ? selected.map((s) => s.value)
                : [];
            // Call parent as (name, value)
            onChange(name, vals);
            return;
        }

        const val = selected ? selected.value : "";
        onChange(name, val);
    };

    const resolvedValue = resolveValueProp();

    const componentsProp = useMemo(
        () => ({
            Option: (props) => (
                <CustomOption
                    {...props}
                    imgWidth={imgWidth}
                    imgheight={imgheight}
                />
            ),
            SingleValue: (props) => (
                <CustomSingleValue
                    {...props}
                    imgWidth={imgWidth}
                    imgheight={imgheight}
                />
            ),
        }),
        [imgWidth, imgheight]
    );

    return (
        <div>
            <Select
                options={options}
                value={resolvedValue}
                onChange={handleChange}
                components={componentsProp}
                styles={mergedStyles}
                placeholder={placeholder}
                isSearchable={isSearchable}
                isDisabled={isDisabled}
                isClearable={isClearable}
                isMulti={isMulti}
                className={className}
                {...props}
            />
        </div>
    );
};

export default ImageSelect;
